import React from 'react';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import OverviewIcon from '@atlaskit/icon/glyph/overview';
import SprintIcon from '@atlaskit/icon/glyph/sprint';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import { Stack, Inline, Text, Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { SprintState } from '@atlassian/jira-shared-types';
import {
	getSprintStateLabel,
	convertCapacity,
	getCapacityUnitMessage,
} from '../../../common/utils';
import { useFormatDateUTC } from '../../../hooks/use-format-date';
import messages from './messages';
import type { Props, SprintDateProps, ProgressBarProps } from './types';

const sprintStatusAppearanceMap: Record<SprintState, ThemeAppearance> = {
	ACTIVE: 'inprogress',
	CLOSED: 'success',
	FUTURE: 'default',
};

const percentage = (count: number, total: number): number =>
	total === 0 ? 0 : Math.round((count / total) * 100);

const ProgressBar = ({
	progressInfo,
	progressPercentage,
	progressWidth,
	additionalBarStyles,
}: ProgressBarProps) => (
	<Inline spread="space-between" shouldWrap rowSpace="space.050">
		<Text size="small" weight="semibold">
			{progressInfo}
		</Text>
		<Text size="small" weight="semibold">
			{progressPercentage}
		</Text>
		<Box xcss={[barStyles, progressBarStyles]}>
			<Box
				xcss={[barStyles, additionalBarStyles]}
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{ width: `${progressWidth}%` }}
			/>
		</Box>
	</Inline>
);

const SprintDate = ({
	dateLabel,
	hasSprintDate,
	date,
	tooltipContent,
	iconLabel,
}: SprintDateProps) => (
	<Inline shouldWrap>
		<Text size="small" weight="semibold" color="color.text.subtle">
			{dateLabel}
		</Text>
		<Inline alignBlock="center" xcss={dateContainerStyles}>
			<Text as={hasSprintDate ? 'span' : 'em'}>{date}</Text>
			<Tooltip content={tooltipContent}>
				<EditorPanelIcon label={iconLabel} size="medium" primaryColor={token('color.icon')} />
			</Tooltip>
		</Inline>
	</Inline>
);

const SprintCapacityFlyout = ({
	onFlyoutClose,
	sprint,
	totalCapacity,
	usedCapacity,
	doneCapacity,
	defaultCapacity,
	iterationEndDate,
	iterationStartDate,
	planningConfig: { planningUnit, workingHoursPerDay },
}: Props) => {
	const intl = useIntl();

	const allocated = convertCapacity(usedCapacity, planningUnit, workingHoursPerDay);
	const total = convertCapacity(totalCapacity ?? defaultCapacity, planningUnit, workingHoursPerDay);
	const done = convertCapacity(doneCapacity, planningUnit, workingHoursPerDay);

	const allocatedPercentage = percentage(allocated, total);
	const donePercentage = percentage(done, allocated);

	const formatDateBasic = useFormatDateUTC(intl, true);
	const formatDateExtended = useFormatDateUTC(intl, true, true, true);
	const sprintStart = sprint.startDate || iterationStartDate;
	const sprintEnd = sprint.completedDate || sprint.endDate || iterationEndDate;
	const sprintStateLabel = getSprintStateLabel(sprint.state, intl.formatMessage);

	return (
		<Stack space="space.150" xcss={stackStyles}>
			<Inline alignBlock="center" spread="space-between">
				<Inline space="space.050" alignBlock="center">
					<SprintIcon
						label={intl.formatMessage(messages.sprintIconLabel)}
						size="small"
						primaryColor={token('color.icon.subtle')}
					/>
					<Text size="small" weight="semibold" color="color.text.subtlest">
						{intl.formatMessage(messages.titleForSprintCapacityFlyout)}
					</Text>
				</Inline>
				<IconButton
					onClick={onFlyoutClose}
					appearance="subtle"
					spacing="compact"
					icon={EditorCloseIcon}
					label={intl.formatMessage(messages.closeIconLabel)}
				/>
			</Inline>

			<Inline>
				<Lozenge appearance={sprintStatusAppearanceMap[sprint.state]}>{sprintStateLabel}</Lozenge>
			</Inline>

			<Heading size="small">{sprint.name}</Heading>

			{sprint.goal && <Text>{sprint.goal}</Text>}

			<Inline space="space.200" spread="space-between" grow="fill" alignBlock="center">
				<Inline space="space.200" alignBlock="center">
					<OverviewIcon
						label={intl.formatMessage(messages.sprintCapacityOverviewLabel)}
						size="medium"
						primaryColor={token('color.icon')}
					/>
					<Text>
						{intl.formatMessage(messages.sprintCapacityLabel, {
							planningUnit: getCapacityUnitMessage(planningUnit, intl.formatMessage),
						})}
					</Text>
				</Inline>
				{/* TODO - placeholder text field - edit/read view text field will be done in JPO-25354
                <Textfield
                    name={intl.formatMessage(messages.sprintCapacityLabel, {
                        planningUnit: getCapacityUnitMessage(planningUnit, intl.formatMessage),
                    })}
                    width={40}
                />
                */}
			</Inline>

			<ProgressBar
				progressInfo={intl.formatMessage(messages.sprintAllocationLabel, {
					numerator: allocated,
					denominator: total,
					unit: getCapacityUnitMessage(planningUnit, intl.formatMessage, total),
				})}
				progressPercentage={`${allocatedPercentage}%`}
				progressWidth={allocated < total ? allocatedPercentage : 100}
				additionalBarStyles={allocated > total ? overloadedBarStyles : allocatedBarStyles}
			/>

			<ProgressBar
				progressInfo={intl.formatMessage(messages.sprintDoneLabel, {
					numerator: done,
					denominator: allocated,
					unit: getCapacityUnitMessage(planningUnit, intl.formatMessage, allocated),
				})}
				progressPercentage={`${donePercentage}%`}
				progressWidth={donePercentage}
				additionalBarStyles={doneBarStyles}
			/>

			{sprintStart && sprintEnd && (
				<Inline space="space.200" alignBlock="center">
					<SprintDate
						dateLabel={intl.formatMessage(messages.sprintStartDateLabel)}
						hasSprintDate={!!sprint.startDate}
						date={formatDateBasic(sprintStart)}
						tooltipContent={formatDateExtended(sprintStart)}
						iconLabel={intl.formatMessage(messages.sprintStartDateInfoIconLabel)}
					/>
					<SprintDate
						dateLabel={intl.formatMessage(messages.sprintEndDateLabel)}
						hasSprintDate={!!sprint.endDate}
						date={formatDateBasic(sprintEnd)}
						tooltipContent={formatDateExtended(sprintEnd)}
						iconLabel={intl.formatMessage(messages.sprintEndDateInfoIconLabel)}
					/>
				</Inline>
			)}

			{(!sprint.startDate || !sprint.endDate) && (
				<Inline>
					<SectionMessage>
						{intl.formatMessage(messages.sprintDatesMissing, {
							sprintName: sprint.name,
						})}
					</SectionMessage>
				</Inline>
			)}
		</Stack>
	);
};

export default SprintCapacityFlyout;

const stackStyles = xcss({
	width: '280px',
	padding: 'space.150',
});

const barStyles = xcss({
	height: '6px',
	borderRadius: 'border.radius.100',
});

const progressBarStyles = xcss({
	width: '100%',
	backgroundColor: 'color.background.neutral',
});

const allocatedBarStyles = xcss({
	backgroundColor: 'color.background.neutral.bold',
});

const doneBarStyles = xcss({
	backgroundColor: 'color.background.success.bold',
});

const overloadedBarStyles = xcss({
	backgroundColor: 'color.background.danger.bold',
});

const dateContainerStyles = xcss({
	lineHeight: 1,
});
